import { Card, Suit, Rank, GameState, Player, PenaltyType, OkeyTile } from '../types';
import { PistiEngine, BatakEngine, KingEngine } from '../lib/engine';
import { OkeyValidator } from '../lib/OkeyValidator';

export class AIService {
  static getBestOkeyDiscard(gameState: GameState, player: Player): string {
    const rack = player.okeyState!.rack;
    if (rack.length === 0) return '';
    
    const indicator = gameState.okeyIndicator;
    const okeyDef = indicator ? OkeyValidator.getOkeyTileDefinition(indicator) : null;
    
    // Simple heuristic: count adjacent tiles
    let worstTile = rack[0];
    let worstScore = 999;
    
    // Assess if we are close to double (Çifte)
    let totalPairs = 0;
    const pairMap = new Map<string, number>();
    for (const t of rack) {
      if (okeyDef && OkeyValidator.isOkey(t, okeyDef)) continue;
      const key = `${t.color}-${t.value}`;
      pairMap.set(key, (pairMap.get(key) || 0) + 1);
    }
    
    for (const [_, count] of pairMap.entries()) {
      if (count >= 2) totalPairs++;
    }

    const goingDouble = totalPairs >= 4;

    // Determine safe discards (tiles that left player didn't seem to want, or discarded by someone else)
    const allDiscards = Object.values(gameState.okeyDiscardPiles || {}).flat();

    for (const t of rack) {
      if (okeyDef && OkeyValidator.isOkey(t, okeyDef)) continue; // Never discard okey
      
      let connectedness = 0;

      if (goingDouble) {
         const key = `${t.color}-${t.value}`;
         const count = pairMap.get(key) || 0;
         if (count >= 2) {
            connectedness += 50; // Heavily favor keeping pairs
         }
      } else {
         // score by same color consecutive
         for (const other of rack) {
            if (other.id === t.id) continue;
            if (okeyDef && OkeyValidator.isOkey(other, okeyDef)) continue;
            
            const eT = OkeyValidator.getTileEffectiveValue(t, okeyDef);
            const eOther = OkeyValidator.getTileEffectiveValue(other, okeyDef);
            
            if (eT.color === eOther.color) {
               const diff = Math.abs(eT.value - eOther.value);
               if (diff === 1 || diff === 12) connectedness += 3;
               if (diff === 2) connectedness += 1.5; // Potential middle fill
            }
            if (eT.value === eOther.value && eT.color !== eOther.color) {
               connectedness += 2; // Group
            }
         }
         
         const isRedundantPair = rack.filter(other => {
            const eT = OkeyValidator.getTileEffectiveValue(t, okeyDef);
            const eOther = OkeyValidator.getTileEffectiveValue(other, okeyDef);
            return eT.color === eOther.color && eT.value === eOther.value && other.id !== t.id;
         }).length > 0;
         
         if (isRedundantPair && !goingDouble) connectedness -= 5; // Discard exact duplicates
      }
      
      // Is it a safe discard? (Someone else threw it recently)
      const isSafe = allDiscards.some(d => d.color === t.color && d.value === t.value);
      if (isSafe) connectedness -= 2;

      if (connectedness < worstScore) {
         worstScore = connectedness;
         worstTile = t;
      }
    }
    
    return worstTile.id;
  }

  static shouldOkeyDrawFromDiscard(gameState: GameState, player: Player): boolean {
    const prevPlayerIndex = (gameState.players.findIndex(p => p.id === player.id) - 1 + gameState.players.length) % gameState.players.length;
    const prevPlayerId = gameState.players[prevPlayerIndex].id;
    const discardPile = gameState.okeyDiscardPiles?.[prevPlayerId];
    if (!discardPile || discardPile.length === 0) return false;
    
    const tile = discardPile[discardPile.length - 1];
    const indicator = gameState.okeyIndicator;
    const okeyDef = indicator ? OkeyValidator.getOkeyTileDefinition(indicator) : null;
    
    if (okeyDef && OkeyValidator.isOkey(tile, okeyDef)) return true; // Always take okey
    
    const eTile = OkeyValidator.getTileEffectiveValue(tile, okeyDef);
    
    // Check if we are going double
    let totalPairs = 0;
    const pairMap = new Map<string, number>();
    for (const t of player.okeyState!.rack) {
      if (okeyDef && OkeyValidator.isOkey(t, okeyDef)) continue;
      const key = `${t.color}-${t.value}`;
      pairMap.set(key, (pairMap.get(key) || 0) + 1);
    }
    for (const [_, count] of pairMap.entries()) {
      if (count >= 2) totalPairs++;
    }
    const goingDouble = totalPairs >= 4;

    if (goingDouble) {
      const tileKey = `${eTile.color}-${eTile.value}`;
      if (pairMap.has(tileKey)) return true; // Completes a pair!
      return false; // Skip drawing from discard if it doesn't help our pairs, deck is better
    }

    let isUseful = false;
    for (const t of player.okeyState!.rack) {
       const eT = OkeyValidator.getTileEffectiveValue(t, okeyDef);
       if (eT.color === eTile.color) {
          const diff = Math.abs(eT.value - eTile.value);
          if (diff === 1 || diff === 12) isUseful = true;
       }
       if (eT.value === eTile.value && eT.color !== eTile.color) {
          isUseful = true;
       }
    }
    
    return isUseful && Math.random() > 0.3; 
  }

  static getBestMove(gameState: GameState, player: Player): string {
    let validHand = player.hand;
    const trickForValidation = gameState.currentTrick.length === gameState.players.length 
      ? [] 
      : gameState.currentTrick;

    if (gameState.status !== 'discarding') {
      if (gameState.gameType === 'batak') {
          validHand = BatakEngine.getValidCards(player.hand, trickForValidation, gameState.trumpSuit, gameState.isTrumpBroken ?? false);
      } else if (gameState.gameType === 'king') {
          validHand = KingEngine.getValidCards(player.hand, trickForValidation, gameState.trumpSuit, gameState.activePenalty, gameState.isTrumpBroken ?? false);
      }
    }

    if (validHand.length === 0) return player.hand.length > 0 ? player.hand[0].id : '';

    if (gameState.status === 'discarding') {
      let sortedHand = [...validHand].sort((a,b) => {
        let valA = BatakEngine.getCardValue(a, gameState.trumpSuit);
        let valB = BatakEngine.getCardValue(b, gameState.trumpSuit);
        return valA - valB;
      });
      return sortedHand[0].id;
    }

    const effectiveGameState = { ...gameState, currentTrick: trickForValidation };

    switch (effectiveGameState.gameType) {
      case 'pisti':
        return this.getPistiMove(effectiveGameState, { ...player, hand: validHand });
      case 'batak':
        return this.getBatakMove(effectiveGameState, { ...player, hand: validHand });
      case 'king':
        return this.getKingMove(effectiveGameState, { ...player, hand: validHand });
      default:
        return validHand[0].id;
    }
  }

  static shouldBotBluff(gameState: GameState, player: Player, chosenCardId: string): boolean {
    if (!gameState.bluffingMode || gameState.middlePile.length === 0) return false;

    const chosenCard = player.hand.find(c => c.id === chosenCardId);
    if (!chosenCard) return false;

    const topPileCard = gameState.middlePile[gameState.middlePile.length - 1];
    const isActualCapture = chosenCard.rank === topPileCard.rank || chosenCard.rank === 'J';

    // If we have an actual capture, we sometimes bluff (tell the truth face-down) to trap the opponent
    if (isActualCapture) {
      // High chance to trap if the pile is big or juicy
      let pilePoints = 0;
      gameState.middlePile.forEach(c => {
         if (['A','J','10','2'].includes(c.rank)) pilePoints += 1;
      });
      const trapChance = pilePoints >= 2 ? 0.35 : 0.15;
      return Math.random() < trapChance;
    }

    // If we DONT have a capture, we might bluff (lie) to steal the pile
    // Steal more often if pile is big, but don't overdo it.
    let pilePoints = 0;
    gameState.middlePile.forEach(c => {
       if (['A','J','10','2'].includes(c.rank)) pilePoints += 1;
    });
    
    // We shouldn't bluff if we already have the most points in the game and don't need risks
    const stealChance = pilePoints >= 2 ? 0.30 : (gameState.middlePile.length >= 3 ? 0.20 : 0.05);

    return Math.random() < stealChance;
  }

  static shouldChallengeBluff(gameState: GameState, player: Player): boolean {
    if (!gameState.activeBluff) return false;
    
    const { pile, playerId } = gameState.activeBluff;
    if (playerId === player.id) return false;

    if (pile.length >= 2) {
      const targetRank = pile[pile.length - 2].rank;

      const allCardsCounted = [
        ...gameState.middlePile.slice(0, -1),
        ...gameState.players.flatMap(p => p.collectedCards),
        ...player.hand
      ];

      const visibleTargetRanks = allCardsCounted.filter(c => c.rank === targetRank).length;
      const visibleJacks = allCardsCounted.filter(c => c.rank === 'J').length;

      // 100% caught lying
      if (visibleTargetRanks >= 4 && visibleJacks >= 4) {
        return true;
      }

      const remainingTargets = 4 - visibleTargetRanks;
      const remainingJacks = 4 - visibleJacks;
      const totalSafeCards = remainingTargets + remainingJacks;

      let bluffProbability = 0;
      if (totalSafeCards <= 1) {
         bluffProbability = 0.85;
      } else if (totalSafeCards <= 2) {
         bluffProbability = 0.50; 
      } else {
         bluffProbability = 0.20;
      }

      let pilePoints = 0;
      pile.slice(0, -1).forEach(c => {
         if (['A','J','10','2'].includes(c.rank)) pilePoints += 1;
      });
      // More likely to challenge if the pile is juicy and worth the risk
      if (pilePoints >= 2) bluffProbability += 0.25;

      return Math.random() < bluffProbability;
    }

    return false;
  }

  private static getPistiMove(gameState: GameState, player: Player): string {
    const hand = player.hand;
    const middlePile = gameState.middlePile;

    // Track all visible cards to calculate card probabilities
    const allVisibleCards = [
      ...gameState.middlePile,
      ...gameState.players.flatMap(p => p.collectedCards)
    ];
    const getVisibleCount = (rank: string) => allVisibleCards.filter(c => c.rank === rank).length;

    if (middlePile.length === 0) {
      // 1. Never lead with a Jack if possible
      const nonJacks = hand.filter(c => c.rank !== 'J');
      if (nonJacks.length > 0) {
        // Strategy: Play a card that is safest (hardest for opponent to match)
        // Safest = Cards where the other 3 are already played, or we have pairs in hand
        let bestCard = nonJacks[0];
        let maxSafetyScore = -1;

        for (const card of nonJacks) {
          const visibleAmount = getVisibleCount(card.rank);
          const pairsInHand = hand.filter(c => c.rank === card.rank).length - 1;
          
          // Safety Score: +2 for each visible card, +1 for each pair in hand
          // - High score means opponent is very unlikely to have this card
          const safetyScore = (visibleAmount * 2) + pairsInHand;
          
          // Valuable cards (A, 10, 2) should have a slight negative bias so we don't waste 
          // points if we can play a junk card with similar safety
          const isValuable = ['A', '10', '2'].includes(card.rank) ? -0.5 : 0;

          if (safetyScore + isValuable > maxSafetyScore) {
            maxSafetyScore = safetyScore + isValuable;
            bestCard = card;
          }
        }
        return bestCard.id;
      }
      return hand[0].id;
    }

    const topCard = middlePile[middlePile.length - 1];

    // 1. Can we Pişti?
    if (middlePile.length === 1) {
      const match = hand.find(c => c.rank === topCard.rank);
      if (match) return match.id;
    }

    // 2. Can we capture with same rank?
    const rankMatch = hand.find(c => c.rank === topCard.rank);
    if (rankMatch) return rankMatch.id;

    // 3. Can we capture with Jack? 
    // Always capture if pile has points, or if opponent might pişti us next turn
    const jack = hand.find(c => c.rank === 'J');
    if (jack) {
      const hasPoints = middlePile.some(c => ['A', 'J', '10', '2'].includes(c.rank));
      const isDangerousPile = middlePile.length >= 3;
      if (hasPoints || isDangerousPile) return jack.id;
    }

    // 4. If we must discard, use the safety logic again
    const nonJacks = hand.filter(c => c.rank !== 'J');
    if (nonJacks.length > 0) {
      let bestDiscard = nonJacks[0];
      let maxSafetyScore = -10;

      for (const card of nonJacks) {
        const visibleAmount = getVisibleCount(card.rank);
        const pairsInHand = hand.filter(c => c.rank === card.rank).length - 1;
        const safetyScore = (visibleAmount * 2) + pairsInHand;
        
        // Avoid dropping valuable cards
        const isPointCard = ['A', '10', '2', 'J'].includes(card.rank) ? -5 : 0;

        if (safetyScore + isPointCard > maxSafetyScore) {
          maxSafetyScore = safetyScore + isPointCard;
          bestDiscard = card;
        }
      }
      return bestDiscard.id;
    }

    return hand[Math.floor(Math.random() * hand.length)].id;
  }

  private static getBatakMove(gameState: GameState, player: Player): string {
    const hand = player.hand;
    const currentTrick = gameState.currentTrick;
    const trumpSuit = gameState.trumpSuit;

    // Helper to calculate safely visible cards
    const allVisibleCards = [
      ...gameState.middlePile, // Might not be used in batak but safe
      ...gameState.players.flatMap(p => p.collectedCards)
    ];

    if (currentTrick.length === 0) {
      // Leading the trick
      // Strategy: Play 'boss' cards (highest unplayed card of a suit) to secure tricks
      const nonTrumps = hand.filter(c => c.suit !== trumpSuit);
      
      if (nonTrumps.length > 0) {
         let bestLead = nonTrumps[0];
         let bestScore = -1000;

         for (const card of nonTrumps) {
            // Check if this card is a boss (all higher cards in this suit are already played)
            const higherRanks = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2']
                .slice(0, ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'].indexOf(card.rank));
            
            const playedHigherCards = allVisibleCards.filter(c => c.suit === card.suit && higherRanks.includes(c.rank));
            const isBoss = playedHigherCards.length === higherRanks.length;
            
            const val = BatakEngine.getCardValue(card, null);
            const score = val + (isBoss ? 50 : 0); // huge bias for boss cards

            if (score > bestScore) {
               bestScore = score;
               bestLead = card;
            }
         }
         return bestLead.id;
      }
      
      // If we only have trumps left
      return [...hand].sort((a,b) => BatakEngine.getCardValue(b, null) - BatakEngine.getCardValue(a, null))[0].id;
    }

    const ledSuit = currentTrick[0].card.suit;
    const cardsOfSuit = hand.filter(c => c.suit === ledSuit);
    const trumps = hand.filter(c => c.suit === trumpSuit);

    // If we have to follow suit
    if (cardsOfSuit.length > 0) {
      const currentWinnerId = BatakEngine.determineWinner(currentTrick, ledSuit, trumpSuit);
      const winningMove = currentTrick.find(t => t.playerId === currentWinnerId);
      const winningValue = winningMove ? BatakEngine.getCardValue(winningMove.card, trumpSuit) : 0;

      const isTrumped = currentTrick.some(t => t.card.suit === trumpSuit && trumpSuit !== null);
      
      if (!isTrumped) {
         const capableToWin = cardsOfSuit.filter(c => BatakEngine.getCardValue(c, trumpSuit) > winningValue);
         if (capableToWin.length > 0) {
             // Play the LOWEST card that still wins the trick (finesse / economic play)
             return [...capableToWin].sort((a,b) => BatakEngine.getCardValue(a, trumpSuit) - BatakEngine.getCardValue(b, trumpSuit))[0].id;
         }
      }
      // If we can't win (or it's already trumped), throw the ABSOLUTE LOWEST card of the suit (ducking)
      return [...cardsOfSuit].sort((a,b) => BatakEngine.getCardValue(a, trumpSuit) - BatakEngine.getCardValue(b, trumpSuit))[0].id;
    }

    // We don't have the led suit. Can we trump?
    if (trumps.length > 0 && trumpSuit) {
      const trumpsInTrick = currentTrick.filter(t => t.card.suit === trumpSuit);
      if (trumpsInTrick.length > 0) {
          const highestTrump = Math.max(...trumpsInTrick.map(t => BatakEngine.getCardValue(t.card, trumpSuit)));
          const capableToBeat = trumps.filter(c => BatakEngine.getCardValue(c, trumpSuit) > highestTrump);
          if (capableToBeat.length > 0) {
              // Play lowest trump that over-trumps
              return [...capableToBeat].sort((a,b) => BatakEngine.getCardValue(a, trumpSuit) - BatakEngine.getCardValue(b, trumpSuit))[0].id;
          }
          // Yerdeki kozu büyütemiyorsa yan kaçar (çöp kart atar), koz harcamaz
          const sideCards = hand.filter(c => c.suit !== trumpSuit);
          if (sideCards.length > 0) {
             return [...sideCards].sort((a,b) => BatakEngine.getCardValue(a, null) - BatakEngine.getCardValue(b, null))[0].id;
          }
          return [...trumps].sort((a,b) => BatakEngine.getCardValue(a, trumpSuit) - BatakEngine.getCardValue(b, trumpSuit))[0].id;
      } else {
          // Play our lowest trump to win the trick
          return [...trumps].sort((a,b) => BatakEngine.getCardValue(a, trumpSuit) - BatakEngine.getCardValue(b, trumpSuit))[0].id;
      }
    }

    // Throw garbage (lowest non-trump card)
    const allGarbage = hand.filter(c => c.suit !== trumpSuit);
    if (allGarbage.length > 0) {
       return [...allGarbage].sort((a,b) => BatakEngine.getCardValue(a, null) - BatakEngine.getCardValue(b, null))[0].id;
    }

    // Fallback
    return [...hand].sort((a,b) => BatakEngine.getCardValue(a, null) - BatakEngine.getCardValue(b, null))[0].id;
  }

  private static getKingMove(gameState: GameState, player: Player): string {
    const hand = player.hand;
    const currentTrick = gameState.currentTrick;
    const penalty = gameState.activePenalty;
    const trumpSuit = gameState.trumpSuit;

    // Helper to identify boss cards (mostly for trumps)
    const allVisibleCards = [
      ...gameState.middlePile,
      ...gameState.players.flatMap(p => p.collectedCards)
    ];

    if (currentTrick.length === 0) {
      // LEADING
      if (trumpSuit) {
        // Trump game: Lead like Batak (boss cards first)
        const nonTrumps = hand.filter(c => c.suit !== trumpSuit);
        if (nonTrumps.length > 0) {
           let bestLead = nonTrumps[0];
           let bestScore = -1000;
           for (const card of nonTrumps) {
              const higherRanks = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2']
                  .slice(0, ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'].indexOf(card.rank));
              const playedHigherCards = allVisibleCards.filter(c => c.suit === card.suit && higherRanks.includes(c.rank));
              const isBoss = playedHigherCards.length === higherRanks.length;
              const val = KingEngine.getCardValue(card);
              const score = val + (isBoss ? 50 : 0);
              if (score > bestScore) {
                 bestScore = score;
                 bestLead = card;
              }
           }
           return bestLead.id;
        }
        return [...hand].sort((a,b) => KingEngine.getCardValue(b) - KingEngine.getCardValue(a))[0].id;
      }

      // Penalty game: We want to lead with our Safest lowest card so we don't take the trick.
      // But avoid leading suits we are completely omitting (if penalty = hearts, we don't want to lead hearts unless forced).
      let safeHand = [...hand];
      if (penalty === 'hearts' || penalty === 'king-hearts') {
         safeHand = hand.filter(c => c.suit !== 'hearts');
         if (safeHand.length === 0) safeHand = hand;
      }

      // If penalty is girls, leading high cards is dangerous since someone might throw a girl.
      // Easiest global defense logic: Play the absolutely lowest value card globally available in safeHand.
      return safeHand.sort((a,b) => KingEngine.getCardValue(a) - KingEngine.getCardValue(b))[0].id;
    }

    // FOLLOWING
    const ledSuit = currentTrick[0].card.suit;
    const cardsOfSuit = hand.filter(c => c.suit === ledSuit);

    if (cardsOfSuit.length > 0) {
       if (trumpSuit) {
          // Trump game follow logic
          const currentWinnerId = KingEngine.determineWinner(currentTrick, ledSuit, trumpSuit);
          const winningMove = currentTrick.find(t => t.playerId === currentWinnerId);
          const winningValue = winningMove ? KingEngine.getCardValue(winningMove.card) : 0;
          
          const isTrumped = currentTrick.some(t => t.card.suit === trumpSuit);
          if (!isTrumped || ledSuit === trumpSuit) {
             const capableToWin = cardsOfSuit.filter(c => KingEngine.getCardValue(c) > winningValue);
             if (capableToWin.length > 0) {
                 return [...capableToWin].sort((a,b) => KingEngine.getCardValue(a) - KingEngine.getCardValue(b))[0].id;
             }
          }
          // Ducking
          return [...cardsOfSuit].sort((a,b) => KingEngine.getCardValue(a) - KingEngine.getCardValue(b))[0].id;
       } 
       else {
          // Penalty game follow logic (Duck!)
          // Find the highest card played IN THE TRICK of the led suit
          const cardsInLedSuit = currentTrick.filter(t => t.card.suit === ledSuit);
          const highestInTrick = Math.max(...cardsInLedSuit.map(t => KingEngine.getCardValue(t.card)));
          
          // Cards from hand that are LOWER than the highest in trick (meaning playing them doesn't take the trick)
          const lowerCards = cardsOfSuit.filter(c => KingEngine.getCardValue(c) < highestInTrick);
          if (lowerCards.length > 0) {
             // Play the HIGHEST card among the lower ones (dumping high cards safely)
             return [...lowerCards].sort((a,b) => KingEngine.getCardValue(b) - KingEngine.getCardValue(a))[0].id;
          }
          // If we are forced to take the trick, play the HIGHEST possible to clear out dangerous cards
          return [...cardsOfSuit].sort((a,b) => KingEngine.getCardValue(b) - KingEngine.getCardValue(a))[0].id;
       }
    }

    // VOID IN LED SUIT
    if (trumpSuit) {
       const trumps = hand.filter(c => c.suit === trumpSuit);
       if (trumps.length > 0) {
          const trumpsInTrick = currentTrick.filter(t => t.card.suit === trumpSuit);
          if (trumpsInTrick.length > 0) {
              const highestTrump = Math.max(...trumpsInTrick.map(t => KingEngine.getCardValue(t.card)));
              const capableToBeat = trumps.filter(c => KingEngine.getCardValue(c) > highestTrump);
              if (capableToBeat.length > 0) {
                  return [...capableToBeat].sort((a,b) => KingEngine.getCardValue(a) - KingEngine.getCardValue(b))[0].id;
              }
              const sideCards = hand.filter(c => c.suit !== trumpSuit);
              if (sideCards.length > 0) {
                  return [...sideCards].sort((a,b) => KingEngine.getCardValue(a) - KingEngine.getCardValue(b))[0].id;
              }
              return [...trumps].sort((a,b) => KingEngine.getCardValue(a) - KingEngine.getCardValue(b))[0].id;
          } else {
              return [...trumps].sort((a,b) => KingEngine.getCardValue(a) - KingEngine.getCardValue(b))[0].id;
          }
       }
    }

    // Dumping Garbage logic
    if (penalty === 'hearts') {
       const heart = hand.filter(c => c.suit === 'hearts').sort((a,b) => KingEngine.getCardValue(b) - KingEngine.getCardValue(a));
       if (heart.length > 0) return heart[0].id;
    }
    if (penalty === 'girls') {
       const queens = hand.filter(c => c.rank === 'Q');
       if (queens.length > 0) return queens[0].id;
    }
    if (penalty === 'boys') {
       const boys = hand.filter(c => c.rank === 'J' || c.rank === 'K');
       if (boys.length > 0) return boys[0].id;
    }
    if (penalty === 'king-hearts') {
        const kingOfHearts = hand.find(c => c.rank === 'K' && c.suit === 'hearts');
        if (kingOfHearts) return kingOfHearts.id;
        // Dump highest heart otherwise
        const heart = hand.filter(c => c.suit === 'hearts').sort((a,b) => KingEngine.getCardValue(b) - KingEngine.getCardValue(a));
        if (heart.length > 0) return heart[0].id;
    }

    // General dump (highest card)
    return [...hand].sort((a,b) => KingEngine.getCardValue(b) - KingEngine.getCardValue(a))[0].id;
  }

  static getBestBid(gameState: GameState, player: Player): number {
    const hand = player.hand;
    let bidCount = 0;

    // Determine the longest suit in hand if we are bidding to pick trumps (ihaleli)
    // or if kozmaca, spades is always trump.
    const isKozmaca = gameState.batakVariant === "kozmaca";
    const trumpSuitGuess = isKozmaca ? 'spades' : 
      ['spades', 'hearts', 'diamonds', 'clubs'].sort((a, b) => 
        hand.filter(c => c.suit === b).length - hand.filter(c => c.suit === a).length
      )[0];

    hand.forEach(c => {
       // Aces are definitely 1 trick, Kings are very likely.
       if (c.rank === 'A') bidCount += 1;
       else if (c.rank === 'K') bidCount += 0.8;
       else if (c.rank === 'Q') bidCount += 0.4;
       else if (c.rank === 'J') bidCount += 0.1;
    });

    // In kozmaca, long spades gives extra tricks. In ihaleli, our chosen trump suit gives extra.
    const trumpCount = hand.filter(c => c.suit === trumpSuitGuess).length;
    if (trumpCount > 4) {
      bidCount += (trumpCount - 4); // Extra trumps likely win tricks
    }

    // Add a bit of randomness to make it feel human
    const finalBid = Math.round(bidCount + (Math.random() * 0.5));

    if (isKozmaca) {
        return Math.max(finalBid, 1);
    }
    if (gameState.batakVariant === "esli") {
        return Math.max(finalBid, 7);
    }
    return Math.max(finalBid, 4); // ihaleli minimum is usually 4 or 5
  }

  static getBestChoice(gameState: GameState, player: Player): PenaltyType | Suit {
    if (gameState.gameType === 'king') {
        const globalPenaltyCounts = ['tricks', 'hearts', 'girls', 'boys', 'king-hearts', 'last-two'].reduce((acc, pen) => {
          acc[pen] = gameState.players.reduce((sum, p) => sum + (p.remainingPenalties.includes(pen as any) ? 0 : 1), 0);
          return acc;
        }, {} as Record<string, number>);
        
        const numCezaPlayed = 6 - player.remainingPenalties.length;
        let availablePenalties = player.remainingPenalties.filter(pen => globalPenaltyCounts[pen] < 2);
        
        if (availablePenalties.length === 0 && numCezaPlayed < 3) {
            // Deadlock fallback: allow any remaining penalty ignoring global limit
            availablePenalties = player.remainingPenalties;
        }
        
        const canPlayCeza = numCezaPlayed < 3 && availablePenalties.length > 0;

        const avgValue = player.hand.reduce((acc, c) => acc + KingEngine.getCardValue(c), 0) / player.hand.length;
        
        // If hand is strong, or we CANNOT play ceza, play trump
        if ((avgValue > 9 && player.remainingTrumps > 0) || !canPlayCeza) {
            if (player.remainingTrumps > 0) {
               const suits: Suit[] = ['spades', 'hearts', 'diamonds', 'clubs'];
               const suitCounts = suits.map(s => ({
                   suit: s,
                   count: player.hand.filter(c => c.suit === s).length
               }));
               return suitCounts.sort((a, b) => b.count - a.count)[0].suit;
            } else if (availablePenalties.length > 0) {
               // Fallback if no trumps left but shouldn't happen usually
               const bestCeza = availablePenalties.includes('hearts') ? 'hearts' : availablePenalties[0];
               return bestCeza as PenaltyType;
            }
        }

        if (availablePenalties.length > 0) {
            if (availablePenalties.includes('hearts')) return 'hearts' as PenaltyType;
            return availablePenalties[0] as PenaltyType;
        }
        
        // Failsafe backup
        return player.remainingPenalties.length > 0 ? player.remainingPenalties[0] as PenaltyType : 'spades' as Suit;
    } else {
        const suits: Suit[] = ['spades', 'hearts', 'diamonds', 'clubs'];
        const suitCounts = suits.map(s => ({
            suit: s,
            count: player.hand.filter(c => c.suit === s).length
        }));
        return suitCounts.sort((a, b) => b.count - a.count)[0].suit;
    }
  }
}
